import React, { useState, useEffect, useCallback } from 'react';
import { Paper, Stack, Switch, Text, Title, Group, Alert, LoadingOverlay, Badge } from '@mantine/core';
import { IconBell, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { notifications } from '@mantine/notifications';
import { PatientsApi } from '../../../global/apis/PatientsApi/PatientsApi';
import { PatientStore } from '../../../global/Store/PatientStore';

interface PatientJournalSettingsProps {
    journalId: number;
    journalName?: string;
    onUpdated?: () => void;
    patientStore?: PatientStore;
}

interface JournalInactivitySettings {
    patientID: number;
    journalID: number;
    journalInactivityReminderEnabled: boolean;
    journalInactivityThresholdDays: number;
    journalInactivityMaxReminders: number;
    isActive: boolean;
    remindersSent: number;
    lastEntryDate: string | null;
    lastReminderDate: string | null;
}

const PatientJournalSettings: React.FC<PatientJournalSettingsProps> = inject('patientStore')(observer(({ 
    journalId,
    onUpdated, 
    patientStore 
}) => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState<JournalInactivitySettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const patientId = patientStore?.patientId ? parseInt(patientStore.patientId) : 0;

    const loadSettings = useCallback(async () => {
        if (!patientId || !journalId) return;
        
        setIsLoading(true);
        try {
            const data = await PatientsApi.getJournalInactivitySettings(patientId, journalId);
            setSettings(data);
        } catch (err) {
            console.error('Error loading journal inactivity settings:', err);
            notifications.show({
                title: t('error'),
                message: t('journalInactivityReminder.errorUpdatingSettings'),
                color: 'red',
            });
        } finally {
            setIsLoading(false);
        }
    }, [patientId, journalId, t]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const handleToggle = async (checked: boolean) => {
        if (!patientId || !journalId) return;

        setIsSaving(true);
        try {
            await PatientsApi.updateJournalInactivitySettings(patientId, journalId, checked);
            
            // Update local state
            setSettings(prev => prev ? { ...prev, isActive: checked } : null);
            
            notifications.show({
                title: t('success'),
                message: t('journalInactivityReminder.settingsSaved'),
                color: 'green',
            });
            
            if (onUpdated)
                onUpdated();
        } catch (err) {
            notifications.show({
                title: t('error'),
                message: t('journalInactivityReminder.errorUpdatingSettings'),
                color: 'red',
            });
            console.error('Error updating journal inactivity reminder settings:', err);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <Paper withBorder p="md" pos="relative">
                <LoadingOverlay visible />
                <Stack gap="md" style={{ minHeight: 100 }} />
            </Paper>
        );
    }

    if (!settings)
        return null;

    // If reminders are not enabled for this journal, show info message
    if (!settings.journalInactivityReminderEnabled) {
        return (
            <Paper withBorder p="md">
                <Alert icon={<IconInfoCircle size={16} />} color="blue">
                    <Text size="sm">
                        {t('journalInactivityReminder.notEnabledForJournal', 
                           'Automated reminders are not enabled for this journal.')}
                    </Text>
                </Alert>
            </Paper>
        );
    }

    return (
        <Paper withBorder p="md">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <div>
                        <Group gap="xs" mb="xs">
                            <IconBell size={20} />
                            <Title order={4}>
                                {t('journalInactivityReminder.patientOverride', 'Reminder Settings')}
                            </Title>
                        </Group>
                        <Text size="sm" c="dimmed">
                            {t('journalInactivityReminder.patientOverrideDescription', 
                               'Control whether this patient receives automated journal inactivity reminders.')}
                        </Text>
                    </div>
                </Group>

                {/* Patient-specific toggle */}
                <Group justify="space-between" align="center">
                    <div style={{ flex: 1 }}>
                        <Group gap="xs">
                            <Text fw={500}>
                                {settings.isActive 
                                    ? t('journalInactivityReminder.remindersEnabled', 'Reminders Enabled')
                                    : t('journalInactivityReminder.remindersDisabled', 'Reminders Disabled')}
                            </Text>
                            {settings.isActive && settings.remindersSent > 0 && (
                                <Badge size="sm" color="blue" variant="light">
                                    {settings.remindersSent} / {settings.journalInactivityMaxReminders} {t('common.sent', 'sent')}
                                </Badge>
                            )}
                        </Group>
                        <Text size="sm" c="dimmed" mt={4}>
                            {settings.isActive
                                ? t('journalInactivityReminder.enabledDescription', 'This patient will receive automated reminders for this journal')
                                : t('journalInactivityReminder.disabledDescription', 'This patient will not receive automated reminders for this journal')}
                        </Text>
                    </div>
                    <Switch
                        checked={settings.isActive}
                        onChange={(e) => handleToggle(e.currentTarget.checked)}
                        disabled={isSaving}
                        size="lg"
                    />
                </Group>

                {/* Last entry/reminder info */}
                {settings.isActive && (settings.lastEntryDate || settings.lastReminderDate) && (
                    <Paper withBorder p="sm" bg="blue.0">
                        <Stack gap="xs">
                            {settings.lastEntryDate && (
                                <Text size="xs" c="dimmed">
                                    {t('journalInactivityReminder.lastEntry', 'Last entry')}: {new Date(settings.lastEntryDate).toLocaleDateString()}
                                </Text>
                            )}
                            {settings.lastReminderDate && (
                                <Text size="xs" c="dimmed">
                                    {t('journalInactivityReminder.lastReminder', 'Last reminder')}: {new Date(settings.lastReminderDate).toLocaleDateString()}
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Paper>
    );
}));

export default PatientJournalSettings;
